const DEFAULT_SETTINGS = Object.freeze( {
    enabled: false,
    quality: 85,
    maxWidth: 1920,
    format: "jpeg"
} );

const OUTPUT_FORMATS = Object.freeze( {
    jpeg: Object.freeze( { mimeType: "image/jpeg", extension: "jpg" } ),
    png: Object.freeze( { mimeType: "image/png", extension: "png" } ),
    webp: Object.freeze( { mimeType: "image/webp", extension: "webp" } )
} );

const normalizeFileExtension = extension => {
    const normalized = String( extension ?? "" ).trim().toLowerCase();
    return normalized === "jpg" || normalized === "jpeg" ? "jpeg" : normalized;
};

export const isAllowedFileExtension = ( extension, allowedTypes ) => {
    const normalizedAllowedTypes = ( Array.isArray( allowedTypes ) ? allowedTypes : [] )
        .map( normalizeFileExtension )
        .filter( Boolean );

    return normalizedAllowedTypes.length === 0 || normalizedAllowedTypes.includes( normalizeFileExtension( extension ) );
};

export const splitUploadItemsByLimit = ( items, maxImages ) => {
    const files = [];
    const tooMany = [];
    let occupiedSlots = 0;

    ( Array.isArray( items ) ? items : [] ).forEach( item => {
        if ( item?.optimizationError === true ) {
            files.push( item );
            return;
        }

        if ( maxImages === 0 || occupiedSlots < maxImages ) {
            files.push( item );
            occupiedSlots++;
        } else {
            tooMany.push( item );
        }
    } );

    return [ files, tooMany ];
};

export class ImageOptimizationError extends Error {
    constructor( code, message, cause = null ) {
        super( message );
        this.name = "ImageOptimizationError";
        this.code = code;
        if ( cause !== null ) this.cause = cause;
    }
}

const IMAGE_OPERATION_TIMEOUT_MS = 30000;
const createCancellationError = () => new ImageOptimizationError( "cancelled", "Image optimization was cancelled." );
const safelyCleanup = callback => {
    try {
        callback();
    } catch ( error ) {
        // One failed cleanup operation must not block settlement or later cleanup.
    }
};

const waitForAbortable = ( operation, signal, onLateResolve = null, timeoutMessage = "Image processing timed out." ) => new Promise( ( resolve, reject ) => {
    let settled = false;
    let timeoutId = null;

    const cleanup = () => {
        if ( timeoutId !== null ) safelyCleanup( () => clearTimeout( timeoutId ) );
        safelyCleanup( () => signal?.removeEventListener( "abort", onAbort ) );
    };
    const onAbort = () => {
        if ( settled ) return;
        settled = true;
        cleanup();
        reject( createCancellationError() );
    };

    safelyCleanup( () => signal?.addEventListener( "abort", onAbort, { once: true } ) );
    try {
        timeoutId = setTimeout( () => {
            if ( settled ) return;
            settled = true;
            cleanup();
            reject( new ImageOptimizationError( "decode-failed", timeoutMessage ) );
        }, IMAGE_OPERATION_TIMEOUT_MS );
    } catch ( error ) {
        // The operation is still observed and can settle even without a timer.
    }

    Promise.resolve( operation ).then( value => {
        if ( settled ) {
            if ( typeof onLateResolve === "function" ) {
                try {
                    onLateResolve( value );
                } catch ( error ) {
                    // Late cleanup cannot change the already-settled result.
                }
            }
            return;
        }
        settled = true;
        cleanup();
        resolve( value );
    }, error => {
        if ( settled ) return;
        settled = true;
        cleanup();
        reject( error );
    } );

    if ( signal?.aborted ) onAbort();
} );

const clampInteger = ( value, minimum, maximum, fallback ) => {
    const parsed = Number( value );
    if ( !Number.isFinite( parsed ) ) return fallback;
    return Math.min( maximum, Math.max( minimum, Math.round( parsed ) ) );
};

export const normalizeImageOptimizationSettings = ( settings = {} ) => {
    const requestedFormat = String( settings.format ?? DEFAULT_SETTINGS.format ).trim().toLowerCase();
    const format = requestedFormat === "jpg" ? "jpeg" : requestedFormat;

    return {
        enabled: settings.enabled === true || settings.enabled === 1 || settings.enabled === "yes" || settings.enabled === "true",
        quality: clampInteger( settings.quality, 10, 100, DEFAULT_SETTINGS.quality ),
        maxWidth: clampInteger( settings.maxWidth, 320, 8192, DEFAULT_SETTINGS.maxWidth ),
        format: Object.prototype.hasOwnProperty.call( OUTPUT_FORMATS, format ) ? format : DEFAULT_SETTINGS.format
    };
};

export const calculateOutputDimensions = ( sourceWidth, sourceHeight, maxWidth ) => {
    const width = Number( sourceWidth );
    const height = Number( sourceHeight );
    const normalizedMaxWidth = clampInteger( maxWidth, 320, 8192, DEFAULT_SETTINGS.maxWidth );

    if ( !Number.isFinite( width ) || !Number.isFinite( height ) || width < 1 || height < 1 ) {
        throw new ImageOptimizationError( "decode-failed", "The decoded image has invalid dimensions." );
    }

    if ( width <= normalizedMaxWidth ) {
        return { width: Math.round( width ), height: Math.round( height ) };
    }

    const scale = normalizedMaxWidth / width;
    return {
        width: normalizedMaxWidth,
        height: Math.max( 1, Math.round( height * scale ) )
    };
};

const decodeWithImageElement = ( file, signal = null ) => new Promise( ( resolve, reject ) => {
    if ( typeof Image !== "function" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function" ) {
        reject( new ImageOptimizationError( "decode-failed", "Browser image decoding is unavailable." ) );
        return;
    }
    if ( signal?.aborted ) {
        reject( createCancellationError() );
        return;
    }

    let image;
    try {
        image = new Image();
    } catch ( error ) {
        reject( new ImageOptimizationError( "decode-failed", "Browser image decoding is unavailable.", error ) );
        return;
    }

    let objectUrl;
    try {
        objectUrl = URL.createObjectURL( file );
    } catch ( error ) {
        reject( new ImageOptimizationError( "decode-failed", "Browser image decoding is unavailable.", error ) );
        return;
    }
    if ( typeof objectUrl !== "string" || objectUrl.length === 0 ) {
        reject( new ImageOptimizationError( "decode-failed", "Browser image decoding is unavailable." ) );
        return;
    }

    let settled = false;
    let timeoutId = null;
    let onAbort = null;

    const removeListeners = () => {
        safelyCleanup( () => image.removeEventListener( "load", onLoad ) );
        safelyCleanup( () => image.removeEventListener( "error", onError ) );
        if ( onAbort ) safelyCleanup( () => signal?.removeEventListener( "abort", onAbort ) );
        if ( timeoutId !== null ) safelyCleanup( () => clearTimeout( timeoutId ) );
    };
    const release = () => {
        removeListeners();
        try {
            image.removeAttribute( "src" );
        } catch ( error ) {
            // URL revocation must still run.
        }
        if ( typeof URL.revokeObjectURL === "function" ) {
            try {
                URL.revokeObjectURL( objectUrl );
            } catch ( error ) {
                // Cleanup failures must not replace the decode result.
            }
        }
    };
    const onLoad = () => {
        if ( settled ) return;
        try {
            const width = image.naturalWidth;
            const height = image.naturalHeight;
            settled = true;
            removeListeners();
            resolve( {
                source: image,
                width,
                height,
                release
            } );
        } catch ( error ) {
            settled = true;
            release();
            reject( new ImageOptimizationError( "decode-failed", "The browser could not decode the image.", error ) );
        }
    };
    const onError = () => {
        if ( settled ) return;
        settled = true;
        release();
        reject( new ImageOptimizationError( "decode-failed", "The browser could not decode the image." ) );
    };
    onAbort = () => {
        if ( settled ) return;
        settled = true;
        release();
        reject( createCancellationError() );
    };

    try {
        image.addEventListener( "load", onLoad, { once: true } );
        image.addEventListener( "error", onError, { once: true } );
        signal?.addEventListener( "abort", onAbort, { once: true } );
        timeoutId = setTimeout( () => {
            if ( settled ) return;
            settled = true;
            release();
            reject( new ImageOptimizationError( "decode-failed", "Image decoding timed out." ) );
        }, IMAGE_OPERATION_TIMEOUT_MS );
        image.src = objectUrl;
    } catch ( error ) {
        settled = true;
        release();
        reject( new ImageOptimizationError( "decode-failed", "The browser could not decode the image.", error ) );
    }
    if ( signal?.aborted ) onAbort();
} );

const decodeBrowserImage = async ( file, signal = null ) => {
    if ( typeof createImageBitmap === "function" ) {
        try {
            const bitmap = await waitForAbortable(
                createImageBitmap( file, { imageOrientation: "from-image" } ),
                signal,
                lateBitmap => lateBitmap?.close?.(),
                "Image bitmap decoding timed out."
            );
            return {
                source: bitmap,
                width: bitmap.width,
                height: bitmap.height,
                release: () => bitmap.close()
            };
        } catch ( error ) {
            if ( error instanceof ImageOptimizationError && error.code === "cancelled" ) throw error;
            // Some browsers reject the orientation option or cannot decode a format
            // through ImageBitmap. The image element path is the compatible fallback.
        }
    }

    return decodeWithImageElement( file, signal );
};

const createBrowserCanvas = () => {
    if ( typeof document === "undefined" || typeof document.createElement !== "function" ) {
        throw new ImageOptimizationError( "canvas-unavailable", "Canvas rendering is unavailable." );
    }
    return document.createElement( "canvas" );
};

const createBrowserFile = ( blob, name, options ) => {
    if ( typeof File !== "function" ) {
        throw new ImageOptimizationError( "file-unavailable", "The browser cannot create an optimized file." );
    }
    return new File( [ blob ], name, options );
};

const exportCanvas = ( canvas, mimeType, quality, signal = null ) => new Promise( ( resolve, reject ) => {
    if ( typeof canvas.toBlob !== "function" ) {
        reject( new ImageOptimizationError( "canvas-unavailable", "Canvas export is unavailable." ) );
        return;
    }
    if ( signal?.aborted ) {
        reject( createCancellationError() );
        return;
    }

    let settled = false;
    let timeoutId = null;
    const cleanup = () => {
        if ( timeoutId !== null ) safelyCleanup( () => clearTimeout( timeoutId ) );
        safelyCleanup( () => signal?.removeEventListener( "abort", onAbort ) );
    };
    const finish = ( callback, value ) => {
        if ( settled ) return;
        settled = true;
        cleanup();
        callback( value );
    };
    const onAbort = () => finish( reject, createCancellationError() );

    try {
        signal?.addEventListener( "abort", onAbort, { once: true } );
        timeoutId = setTimeout(
            () => finish( reject, new ImageOptimizationError( "export-failed", "Image export timed out." ) ),
            IMAGE_OPERATION_TIMEOUT_MS
        );
        canvas.toBlob( blob => {
            if ( settled ) return;
            try {
                if ( !( blob instanceof Blob ) ) {
                    finish( reject, new ImageOptimizationError( "export-failed", "The browser could not export the optimized image." ) );
                    return;
                }
                if ( blob.type.toLowerCase() !== mimeType ) {
                    finish( reject, new ImageOptimizationError( "unsupported-format", "The browser substituted a different output format." ) );
                    return;
                }
                finish( resolve, blob );
            } catch ( error ) {
                finish( reject, new ImageOptimizationError( "export-failed", "The browser could not export the optimized image.", error ) );
            }
        }, mimeType, quality );
    } catch ( error ) {
        finish( reject, new ImageOptimizationError( "export-failed", "The browser could not export the optimized image.", error ) );
    }
    if ( signal?.aborted ) onAbort();
} );

const replaceExtension = ( fileName, extension ) => {
    const name = String( fileName || "image" );
    const baseName = name.replace( /\.[^.]+$/, "" ) || "image";
    return `${ baseName }.${ extension }`;
};

export const optimizeImage = async ( file, rawSettings = {}, dependencies = {} ) => {
    const settings = normalizeImageOptimizationSettings( rawSettings );
    if ( !settings.enabled ) {
        return { file, dimensions: null, originalFileName: "", optimized: false };
    }

    const format = OUTPUT_FORMATS[ settings.format ];
    const decodeImage = dependencies.decodeImage ?? decodeBrowserImage;
    const createCanvas = dependencies.createCanvas ?? createBrowserCanvas;
    const createFile = dependencies.createFile ?? createBrowserFile;
    const signal = dependencies.signal ?? null;
    let decoded = null;
    let canvas = null;

    try {
        try {
            decoded = await waitForAbortable(
                Promise.resolve().then( () => decodeImage( file, signal ) ),
                signal,
                lateDecoded => lateDecoded?.release?.(),
                "Image decoding timed out."
            );
        } catch ( error ) {
            if ( error instanceof ImageOptimizationError ) throw error;
            throw new ImageOptimizationError( "decode-failed", "The browser could not decode the image.", error );
        }

        const dimensions = calculateOutputDimensions( decoded.width, decoded.height, settings.maxWidth );
        canvas = createCanvas();
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;

        const context = canvas.getContext( "2d" );
        if ( context === null ) {
            throw new ImageOptimizationError( "canvas-unavailable", "Canvas rendering is unavailable." );
        }

        try {
            if ( settings.format === "jpeg" ) {
                context.fillStyle = "#ffffff";
                context.fillRect( 0, 0, dimensions.width, dimensions.height );
            }
            context.drawImage( decoded.source, 0, 0, dimensions.width, dimensions.height );
        } catch ( error ) {
            throw new ImageOptimizationError( "render-failed", "The browser could not render the image for optimization.", error );
        }

        const blob = await exportCanvas( canvas, format.mimeType, settings.quality / 100, signal );
        if ( signal?.aborted ) throw createCancellationError();
        const optimizedFile = createFile(
            blob,
            replaceExtension( file.name, format.extension ),
            { type: format.mimeType, lastModified: file.lastModified ?? Date.now() }
        );

        return {
            file: optimizedFile,
            dimensions,
            originalFileName: file.name,
            optimized: true
        };
    } finally {
        if ( decoded && typeof decoded.release === "function" ) {
            try {
                decoded.release();
            } catch ( error ) {
                // Cleanup failures must not replace the optimization result.
            }
        }
        if ( canvas ) {
            try {
                canvas.width = 1;
                canvas.height = 1;
            } catch ( error ) {
                // Canvas cleanup is best-effort.
            }
        }
    }
};

export const appendUploadItemFiles = ( formData, uploadItem, index ) => {
    if ( typeof uploadItem.error === "string" && uploadItem.error.length > 0 ) {
        throw new Error( "Cannot append an upload item with a blocking error." );
    }

    formData.append( `foogallery_images[${ index }]`, uploadItem.file, uploadItem.file.name );
    formData.append( `foogallery_file_kinds[${ index }]`, uploadItem.kind );

    if ( typeof uploadItem.originalFileName === "string" && uploadItem.originalFileName.length > 0 ) {
        formData.append( `foogallery_original_filenames[${ index }]`, uploadItem.originalFileName );
    }

    if ( uploadItem.kind === "video" && typeof File === "function" && uploadItem.posterFile instanceof File ) {
        formData.append( `foogallery_video_posters[${ index }]`, uploadItem.posterFile, uploadItem.posterFile.name );
    }
};
